
import { IIOPensionInputs, IOFormOfPayment } from 'app/calcs/customers/IO/interfaces'
import { ILocal145PensionInputs, Local145FormOfPayment } from 'app/calcs/customers/local145/interfaces'
import { INEBFPensionInputs, NEBFFormOfPayment } from 'app/calcs/customers/NEBF/interfaces'
import { ISharedPensionInputs } from 'app/calcs/customers/shared/interfaces'
import { SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { IRetirementPerson, isValidPerson } from 'app/redux/Profile/model'
import { UserActions } from 'app/redux/User/actions'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo';
import { IPokeandPullRequest, IRequestedValues, ISheetValues, IWorkbookValues, WorkbookServiceResponse } from 'app/services/models/Workbook'
import { WorkbookService } from 'app/services/WorkbookService'
import { makeDebug, toYesNo } from 'app/utils';
import { INosyParker, nullNosyParker } from 'app/utils/nosyParker'
import * as IODAO from 'app/workbookDAOs/IODAO'
import * as NEBFDAO from 'app/workbookDAOs/NEBFDAO'
import { getWebServicesConfig } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'

interface ILocal145RunCalculationsRequest {
  member: IRetirementPerson
  spouse: IRetirementPerson
  vals: ILocal145PensionInputs
  ioInputs: IIOPensionInputs
  nebfInputs: INEBFPensionInputs
  shared: ISharedPensionInputs
  when: moment.Moment
}

const sheetDateFormat = 'L'
const inputsSheetName = 'Input'
const outputsSheetName = 'Output 145'

const calculatorSheetName = 'Calculator_145'
const calcSheetRange = 'a1:s36'

interface IFormOfPaymentEntry {
  form: Local145FormOfPayment | SimplePensionFormOfPayment
  range: string
}

// these are found in the 'Output 145' tab
const formOfPaymentMap = [
  { form: Local145FormOfPayment.lifeOnly, range: 'c4:i43' },
  { form: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor, range: 'm4:s43' },
  { form: SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor, range: 'w4:ac43' },
  { form: SimplePensionFormOfPayment.fullJointAndSurvivor, range: 'ag4:am43' },
  { form: Local145FormOfPayment.partialLumpSum, range: 'aq4:aw43' }
]

const log = makeDebug(true)

function commonPaymentExtractor(when: moment.Moment, output: ISheetValues, tableKey: IFormOfPaymentEntry) {

  // find the table corresponding to the form of payment
  const table = output[tableKey.range]

  // find the row for the year we need
  let row = _.find(table, (r) => r[0] === when.year())
  if (!row) { row = _.first(table) }
  if (!row) {
    // tslint:disable-next-line:no-debugger
    debugger
    return 0
  }

  // some worksheets use 'financial' formatting which formats 0's as '-'
  if ('-' === row[6]) { row[6] = 0 }
  return row[6] || 0
}

function makeMiscExtractor() {
  return (wr: WorkbookServiceResponse) => {
    const r = {
      totalLumpSum: 0,
      totalVestingService: 0,
      totalPensionCredits: 0
    }
    if (!wr.Results) { return r }
    const calcOutputs = wr.Results[calculatorSheetName];
    const calcRange = calcOutputs[calcSheetRange]
    const findData = (what: string) => _.find(calcRange, (z) => z[1] === what)
    const rr = findData(' - lump sum ')

    if (rr) {
      // pick the data out of an adjacent column
      const v = rr[3] as number
      r.totalLumpSum = v || 0
      r.totalVestingService = v || 0
    }

    return r
  }
}

function makePaymentExtractor(vals: ILocal145PensionInputs, when: moment.Moment) {
  return (wr: WorkbookServiceResponse): number => {
    if (_.isUndefined(vals.formOfPayment)) { return 0 }
    if (!wr.Results) { return 0 }
    const output = wr.Results[outputsSheetName]
    const tableKey = _.find(formOfPaymentMap, (q) => q.form === vals.formOfPayment)
    return commonPaymentExtractor(when, output, tableKey)
  }
}

type Local145WorkbookDAOCalculator = (request: ILocal145RunCalculationsRequest) => Promise<WorkbookServiceResponse>

class Local145WorkbookDAO {
  private service: WorkbookService

  constructor(protected nosyParker: INosyParker = nullNosyParker) {
    this.service = new WorkbookService(getWebServicesConfig().workbookEndpoint, 'local145Workbook')
  }

  exportExhibitTab(request: ILocal145RunCalculationsRequest): Promise<WorkbookServiceResponse> {
    log('Local145WorkbookDAO using service: ' + this.service)

    this.nosyParker.operationIsStarting()
    const startTime = moment.utc()

    const inputs = this.fillRequestArgs(request)
    const outputs = {}
    const filename = 'Local145PensionExhibit-' + moment().format('MM-YYYY') + '.pdf'
    const req: IPokeandPullRequest = { inputs, outputs, filename }

    return this.service.exportExhibitTab(UserActions.getSharedVar('accessToken'), req)
      .then((wr: WorkbookServiceResponse) => {
        const elapsed = moment.utc().diff(startTime)

        log('It took ' + elapsed + 'ms for Local145WorkbookDAO exportExhibitTab')

        if (wr.IsError || !wr.Results) {
          // tslint:disable-next-line:no-debugger
          debugger
          this.nosyParker.operationHasFailed(wr)
        } else {
          this.nosyParker.operationHasCompleted(wr)
        }

        return wr
      })
      .catch((reason) => {
        this.nosyParker.operationHasFailed(reason)
        return Promise.reject(reason)
      })
  }

  /**
   * Run the Local145 calculations workbook.
   *
   * @param request The request parameters
   *
   * @returns a WorkbookServiceResponse.
   */
  runCalculations(request: ILocal145RunCalculationsRequest): Promise<WorkbookServiceResponse> {

    log(`Local145WorkbookDAO using service: ${this.service}`)

    this.nosyParker.operationIsStarting()
    const startTime = moment.utc()

    return this.issueRequest(request)
      .then((wr: WorkbookServiceResponse) => {
        const elapsed = moment.utc().diff(startTime)

        log(`It took ${elapsed}ms for Local145WorkbookDAO calculations`)

        if (wr.IsError || !wr.Results) {
          // tslint:disable-next-line:no-debugger
          debugger
          this.nosyParker.operationHasFailed(wr)
        } else {
          this.nosyParker.operationHasCompleted(wr)
        }

        return wr
      })
      .catch((reason) => {
        this.nosyParker.operationHasFailed(reason)
        return Promise.reject(reason)
      })
  }

  private fillRequestArgs(request: ILocal145RunCalculationsRequest): IWorkbookValues {
    const spousePresent = isValidPerson(request.spouse)
    const spouseDOB = spousePresent ? request.spouse.dob.format(sheetDateFormat) : ''
    const spouseLifeExpectancy = spousePresent ? request.spouse.selectedAgeExpectancy : ''

    const inputs: IWorkbookValues = {
      [inputsSheetName]: {
        'c5:c10': [ // general inputs
          [request.member.firstName],
          [request.member.lastName],
          [request.member.dob.format(sheetDateFormat)],
          [spouseDOB],
          [request.member.selectedAgeExpectancy],
          [spouseLifeExpectancy]          
        ],
        'c13:c28': [ // Local 145 Plan Inputs
          [request.vals.beginPaymentsAge.years()],
          [request.vals.statusCode],
          [request.vals.insideHours],
          [request.vals.residentialHours],
          [request.vals.teledataHours],
          [request.vals.insideHours + request.vals.residentialHours + request.vals.teledataHours],
          [request.vals.insideCreditsValue],
          [request.vals.residentialCreditsValue],
          [request.vals.teledateCreditsValue],
          [request.vals.insideCredits],
          [request.vals.residentialCredits],
          [request.vals.teledataCredits],
          [request.vals.insideCredits + request.vals.residentialCredits + request.vals.teledataCredits],
          [0],
          [0],
          [0]
        ],
        'c30:c31': [ // NEBF inputs
          [request.nebfInputs.beginPaymentsAge.years()],
          [request.nebfInputs.currentYearsService.asYears()]
        ],
        'c33:c34': [ // IO inputs
          [request.ioInputs.beginPaymentsAge.years()],
          [request.ioInputs.yearsOfAMembership.asYears()]
        ],
        'c36': theCensusInfo.censusDate.format(sheetDateFormat)
      }
    }

    return inputs
  }

  private issueRequest(request: ILocal145RunCalculationsRequest): Promise<WorkbookServiceResponse> {
    const inputs = this.fillRequestArgs(request)

    const outputs: IRequestedValues = {
      [outputsSheetName]: _.map(formOfPaymentMap, (fop) => fop.range),
      [calculatorSheetName]: [calcSheetRange],

      [IODAO.ioSheetName]: IODAO.ioSheetRange,
      [IODAO.ioCalculatorSheetName]: [IODAO.ioCalculatorSheetRange], // pulled for debugging purposes

      [NEBFDAO.nebfSheetName]: NEBFDAO.nebfSheetRange,
      [NEBFDAO.nebfCalculatorSheetName]: [NEBFDAO.nebfCalculatorSheetRange]
    }

    const req: IPokeandPullRequest = { inputs, outputs }
    return this.service.pokeAndPull(UserActions.getSharedVar('accessToken'), req)
  }

}

const makeNEBFPaymentExtractor = NEBFDAO.makeNEBFPaymentExtractor

export {
  ILocal145RunCalculationsRequest,
  Local145WorkbookDAO,
  Local145WorkbookDAOCalculator,

  makeMiscExtractor,
  makePaymentExtractor,

  makeNEBFPaymentExtractor
}



// WEBPACK FOOTER //
// ./src/app/workbookDAOs/local145DAO/index.ts